import { useEffect, useRef, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { MessageCircleQuestion } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import { CITIES } from "@/lib/api";

const CategoryPage = lazy(() => import("@/pages/CategoryPage"));
const QuestionPage = lazy(() => import("@/pages/QuestionPage"));
const RoundupPage = lazy(() => import("@/pages/RoundupPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const ComparisonPage = lazy(() => import("@/pages/ComparisonPage"));
const HomesForSalePage = lazy(() => import("@/pages/HomesForSalePage"));
const Admin = lazy(() => import("@/pages/Admin"));

function PageFallback() {
  return (
    <div data-testid="page-loading" className="flex min-h-[60vh] items-center justify-center">
      <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-brand-navy/20 border-t-brand-red" />
    </div>
  );
}

function StickyAsk() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  if (pathname.startsWith("/admin")) return null;
  const go = () => {
    const el = document.getElementById("ask-frank");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      return;
    }
    const cityPath = CITIES.find((c) => c.path === pathname)?.path ?? "/";
    navigate(`${cityPath}#ask-frank`);
  };
  return (
    <button
      data-testid="sticky-ask-frank"
      onClick={go}
      className="fixed bottom-5 left-5 z-40 inline-flex items-center gap-2 rounded-full bg-brand-red px-5 py-3 text-sm font-bold text-white shadow-xl shadow-brand-red/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-red-dark"
    >
      <MessageCircleQuestion className="h-4 w-4" /> Ask Frank
    </button>
  );
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 120);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Shell() {
  const askFrankRef = useRef(null);
  const scrollToAsk = () => {
    const el = document.getElementById("ask-frank");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = "/#ask-frank";
    }
  };
  return (
    <div className="App">
      <ScrollManager />
      <Navbar onAskFrank={scrollToAsk} />
      <Suspense fallback={<PageFallback />}>
        <Routes>
        <Route path="/" element={<Home city="sierra-vista" askFrankRef={askFrankRef} />} />
        <Route path="/hereford" element={<Home city="hereford" askFrankRef={askFrankRef} />} />
        <Route path="/huachuca-city" element={<Home city="huachuca-city" askFrankRef={askFrankRef} />} />
        <Route path="/bisbee" element={<Home city="bisbee" askFrankRef={askFrankRef} />} />
        <Route path="/tombstone" element={<Home city="tombstone" askFrankRef={askFrankRef} />} />
        <Route path="/benson" element={<Home city="benson" askFrankRef={askFrankRef} />} />
        <Route path="/douglas" element={<Home city="douglas" askFrankRef={askFrankRef} />} />
        <Route path="/willcox" element={<Home city="willcox" askFrankRef={askFrankRef} />} />
        <Route path="/sahuarita" element={<Home city="sahuarita" askFrankRef={askFrankRef} />} />
        <Route path="/vail" element={<Home city="vail" askFrankRef={askFrankRef} />} />
        <Route path="/sonoita" element={<Home city="sonoita" askFrankRef={askFrankRef} />} />
        <Route path="/category/:slug" element={<CategoryPage />} />
        <Route path="/answers/:slug" element={<QuestionPage />} />
        <Route path="/roundup/:slug" element={<RoundupPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/compare/:pair" element={<ComparisonPage />} />
        <Route path="/homes-for-sale" element={<HomesForSalePage />} />
        <Route path="/admin" element={<Admin />} />
        </Routes>
      </Suspense>
      <Footer />
      <StickyAsk />
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <Shell />
    </BrowserRouter>
  );
}

export default App;
